import { useEffect, useState, useCallback } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Play, CheckCircle2, Camera, Loader2, X, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Plate } from "@/components/StatusBadge";

const JOB_STATUS = {
  ASSIGNED: { t: "ASSIGNED", c: "bg-gray-100 text-gray-700" },
  IN_PROGRESS: { t: "IN PROGRESS", c: "bg-blue-100 text-blue-700" },
  WAITING_QC: { t: "WAITING QC", c: "bg-amber-100 text-amber-700" },
  COMPLETED_QC_PASS: { t: "QC PASS", c: "bg-emerald-100 text-emerald-700" },
  REWORK_REQUIRED: { t: "REWORK", c: "bg-red-100 text-red-700" },
  REJECTED: { t: "REJECTED", c: "bg-red-100 text-red-700" },
};
const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

function JobBadge({ s }) {
  const m = JOB_STATUS[s] || { t: s, c: "bg-gray-100 text-gray-700" };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.c}`}>{m.t}</span>;
}

export default function TechnicianWorklist() {
  const [jobs, setJobs] = useState([]);
  const [ledger, setLedger] = useState({ summary: {} });
  const [loading, setLoading] = useState(true);
  const [completeJob, setCompleteJob] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, l] = await Promise.all([api.get("/tech/jobs"), api.get("/tech/incentive-ledger")]);
      setJobs(j.data); setLedger(l.data);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const start = async (id) => {
    try { await api.post(`/tech/jobs/${id}/start`); toast.success("Job dimulai"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const active = jobs.filter((j) => ["ASSIGNED", "IN_PROGRESS", "REWORK_REQUIRED", "WAITING_QC"].includes(j.status));
  const done = jobs.filter((j) => ["COMPLETED_QC_PASS", "REJECTED"].includes(j.status));
  const s = ledger.summary || {};

  return (
    <div data-testid="tech-worklist" className="mx-auto max-w-3xl">
      <h1 className="font-heading text-xl font-bold text-[#111827]">Worklist Saya</h1>
      <p className="mt-0.5 text-xs text-gray-500">Job yang ditugaskan ke Anda · tekan START lalu COMPLETE + foto bukti</p>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <Stat label="Job Aktif" value={active.length} />
        <Stat label="QC Pass" value={jobs.filter((j) => j.status === "COMPLETED_QC_PASS").length} />
        <Stat label="Insentif Disetujui" value={rupiah(s.approved)} small />
      </div>

      {loading ? <p className="py-10 text-center text-sm text-gray-400">Memuat…</p> : (
        <>
          <h2 className="mt-4 text-sm font-semibold text-gray-700">Aktif ({active.length})</h2>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {active.map((j) => (
              <div key={j.id} data-testid={`tech-job-${j.id}`} className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
                {j.wo_number && <div className="mb-1.5"><span data-testid={`tech-wo-${j.id}`} className="inline-flex items-center rounded bg-blue-500/15 px-2 py-0.5 font-mono text-xs font-bold text-[#60A5FA] ring-1 ring-inset ring-blue-400/30">{j.wo_number}</span></div>}
                <div className="flex items-center justify-between gap-2">
                  <Plate value={j.license_plate} />
                  <JobBadge s={j.status} />
                </div>
                <div className="mt-1.5 truncate text-[13px] font-semibold text-gray-800">{j.work_description || "-"}</div>
                <div className="truncate text-xs text-gray-500">{j.panel || "-"} · {j.work_type} · {j.vehicle}</div>
                <div className="mt-1 text-[11px] text-gray-400">{j.claim_no} · Stage {j.stage || "-"}</div>
                {j.status === "REWORK_REQUIRED" && j.qc_reason && <div className="mt-1 rounded bg-red-50 px-2 py-1 text-[11px] text-red-600">Rework: {j.qc_reason}</div>}
                <div className="mt-2 flex gap-2">
                  {["ASSIGNED", "REWORK_REQUIRED"].includes(j.status) && (
                    <Button data-testid={`tech-start-${j.id}`} size="sm" className="h-8 flex-1 bg-blue-600 text-xs hover:bg-blue-700" onClick={() => start(j.id)}><Play size={13} className="mr-1" /> START JOB</Button>
                  )}
                  {j.status === "IN_PROGRESS" && (
                    <Button data-testid={`tech-complete-${j.id}`} size="sm" className="h-8 flex-1 bg-emerald-600 text-xs hover:bg-emerald-700" onClick={() => setCompleteJob(j)}><CheckCircle2 size={13} className="mr-1" /> COMPLETE JOB</Button>
                  )}
                  {j.status === "WAITING_QC" && <div className="flex-1 text-center text-xs text-amber-600">Menunggu QC…</div>}
                </div>
              </div>
            ))}
            {active.length === 0 && <p className="py-6 text-center text-sm text-gray-400 sm:col-span-2">Tidak ada job aktif.</p>}
          </div>

          {done.length > 0 && (
            <>
              <h2 className="mt-4 text-sm font-semibold text-gray-700">Selesai ({done.length})</h2>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {done.map((j) => (
                  <div key={j.id} className="rounded-lg border border-gray-100 bg-white p-2.5 opacity-80 shadow-sm">
                    <div className="flex items-center justify-between gap-2"><Plate value={j.license_plate} /><JobBadge s={j.status} /></div>
                    <div className="mt-1 truncate text-xs text-gray-600">{j.work_description} · {j.work_type}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {completeJob && <CompleteDialog job={completeJob} onClose={() => setCompleteJob(null)} onDone={() => { setCompleteJob(null); load(); }} />}
    </div>
  );
}

function Stat({ label, value, small }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-white p-2.5 text-center shadow-sm">
      <div className={`font-bold text-[#111827] ${small ? "text-sm" : "text-xl"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
    </div>
  );
}

function CompleteDialog({ job, onClose, onDone }) {
  const [photos, setPhotos] = useState([]);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/tech/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data]);
      }
    } catch (err) { toast.error(formatApiErrorDetail(err)); }
    setUploading(false); e.target.value = "";
  };

  const submit = async () => {
    if (!photos.length) { toast.error("Minimal 1 foto bukti"); return; }
    setSaving(true);
    try { await api.post(`/tech/jobs/${job.id}/complete`, { photos, note }); toast.success("Job selesai, menunggu QC"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="text-base"><Wrench size={16} className="mr-1 inline text-[#3B82F6]" />Complete Job · {job.license_plate}</DialogTitle></DialogHeader>
        <div className="text-xs text-gray-500">{job.work_description} · {job.panel}</div>
        <label data-testid="tech-photo-input" className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 py-4 text-sm text-gray-500 hover:border-[#3B82F6]">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />} {uploading ? "Mengunggah…" : "Ambil / Pilih Foto (bisa banyak)"}
          <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={onPick} />
        </label>
        {photos.length > 0 && (
          <div className="mt-2 grid grid-cols-4 gap-2">
            {photos.map((p, i) => (
              <div key={i} className="relative">
                <img src={`${API}/tech/file?path=${encodeURIComponent(p.storage_path)}`} alt="" className="h-16 w-full rounded object-cover" />
                <button onClick={() => setPhotos((x) => x.filter((_, idx) => idx !== i))} className="absolute -right-1 -top-1 rounded-full bg-red-500 p-0.5 text-white"><X size={11} /></button>
              </div>
            ))}
          </div>
        )}
        <Textarea data-testid="tech-note" placeholder="Catatan singkat (opsional)" value={note} onChange={(e) => setNote(e.target.value)} className="mt-2 text-sm" rows={2} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button data-testid="tech-submit-complete" disabled={saving || !photos.length} onClick={submit} className="bg-emerald-600 hover:bg-emerald-700">{saving ? "Menyimpan…" : "Submit → QC"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
